export interface EmailAccount {
  id: string;
  user_id: string;
  organization_id: string;
  email_address: string;
  display_name?: string;
  provider: 'gmail' | 'outlook' | 'webde' | 'custom';
  smtp_host?: string;
  smtp_port?: number;
  smtp_username?: string;
  smtp_password?: string;
  imap_host?: string;
  imap_port?: number;
  imap_username?: string;
  imap_password?: string;
  is_default: boolean;
  is_active: boolean;
  oauth_refresh_token?: string;
  oauth_access_token?: string;
  oauth_expires_at?: string;
  last_sync_at?: string;
  created_at: string;
  updated_at: string;
}

export interface OrganizationSignature {
  id: string;
  organization_id: string;
  signature_name: string;
  signature_html: string;
  signature_text?: string;
  is_default: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface UserSignature {
  id: string;
  user_id: string;
  signature_name: string;
  signature_html: string;
  signature_text?: string;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface EmailTemplate {
  id: string;
  organization_id: string;
  template_name: string;
  subject_template?: string;
  body_html: string;
  body_text?: string;
  template_type: string;
  created_by: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Email {
  id: string;
  user_id: string;
  organization_id: string;
  account_id: string;
  message_id?: string;
  thread_id?: string;
  from_address: string;
  to_addresses: string[];
  cc_addresses?: string[];
  bcc_addresses?: string[];
  subject?: string;
  body_html?: string;
  body_text?: string;
  signature_used?: string;
  template_used?: string;
  status: 'draft' | 'sent' | 'received' | 'failed';
  priority: 'high' | 'normal' | 'low';
  is_read: boolean;
  is_starred: boolean;
  labels?: string[];
  attachments?: EmailAttachment[];
  sent_at?: string;
  received_at?: string;
  created_at: string;
  updated_at: string;
}

export interface EmailAttachment {
  id: string;
  filename: string;
  content_type: string;
  size: number;
  url?: string;
}

export interface EmailProvider {
  name: string;
  smtp_host: string;
  smtp_port: number;
  imap_host: string;
  imap_port: number;
  use_ssl: boolean;
  oauth_enabled: boolean;
}

export const EMAIL_PROVIDERS: Record<string, EmailProvider> = {
  gmail: {
    name: 'Google Mail',
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    imap_host: 'imap.gmail.com',
    imap_port: 993,
    use_ssl: true,
    oauth_enabled: true,
  },
  outlook: {
    name: 'Microsoft Outlook',
    smtp_host: 'smtp-mail.outlook.com',
    smtp_port: 587,
    imap_host: 'outlook.office365.com',
    imap_port: 993,
    use_ssl: true,
    oauth_enabled: true,
  },
  webde: {
    name: 'WEB.DE',
    smtp_host: 'smtp.web.de',
    smtp_port: 587,
    imap_host: 'imap.web.de',
    imap_port: 993,
    use_ssl: true,
    oauth_enabled: false,
  },
  custom: {
    name: 'Benutzerdefiniert',
    smtp_host: '',
    smtp_port: 587,
    imap_host: '',
    imap_port: 993,
    use_ssl: true,
    oauth_enabled: false,
  },
};

// Erweiterte Provider-Datenbank für automatische Erkennung
export const EMAIL_DOMAIN_PROVIDERS: Record<string, EmailProvider> = {
  // Google
  'gmail.com': EMAIL_PROVIDERS.gmail,
  'googlemail.com': EMAIL_PROVIDERS.gmail,
  
  // Microsoft
  'outlook.com': EMAIL_PROVIDERS.outlook,
  'outlook.de': EMAIL_PROVIDERS.outlook,
  'hotmail.com': EMAIL_PROVIDERS.outlook,
  'hotmail.de': EMAIL_PROVIDERS.outlook,
  'live.com': EMAIL_PROVIDERS.outlook,
  'live.de': EMAIL_PROVIDERS.outlook,
  'msn.com': EMAIL_PROVIDERS.outlook,
  
  // Deutsche Provider
  'web.de': EMAIL_PROVIDERS.webde,
  'gmx.de': {
    name: 'GMX',
    smtp_host: 'mail.gmx.net',
    smtp_port: 587,
    imap_host: 'imap.gmx.net',
    imap_port: 993,
    use_ssl: true,
    oauth_enabled: false,
  },
  'gmx.net': {
    name: 'GMX',
    smtp_host: 'mail.gmx.net',
    smtp_port: 587,
    imap_host: 'imap.gmx.net',
    imap_port: 993,
    use_ssl: true,
    oauth_enabled: false,
  },
  't-online.de': {
    name: 'T-Online',
    smtp_host: 'securesmtp.t-online.de',
    smtp_port: 587,
    imap_host: 'secureimap.t-online.de',
    imap_port: 993,
    use_ssl: true,
    oauth_enabled: false,
  },
  'freenet.de': {
    name: 'Freenet',
    smtp_host: 'mx.freenet.de',
    smtp_port: 587,
    imap_host: 'mx.freenet.de',
    imap_port: 993,
    use_ssl: true,
    oauth_enabled: false,
  },
  '1und1.de': {
    name: '1&1',
    smtp_host: 'smtp.1und1.de',
    smtp_port: 587,
    imap_host: 'imap.1und1.de',
    imap_port: 993,
    use_ssl: true,
    oauth_enabled: false,
  },
  
  // Internationale Provider
  'yahoo.com': {
    name: 'Yahoo',
    smtp_host: 'smtp.mail.yahoo.com',
    smtp_port: 587,
    imap_host: 'imap.mail.yahoo.com',
    imap_port: 993,
    use_ssl: true,
    oauth_enabled: false,
  },
  'yahoo.de': {
    name: 'Yahoo',
    smtp_host: 'smtp.mail.yahoo.com',
    smtp_port: 587,
    imap_host: 'imap.mail.yahoo.com',
    imap_port: 993,
    use_ssl: true,
    oauth_enabled: false,
  },
  'aol.com': {
    name: 'AOL',
    smtp_host: 'smtp.aol.com',
    smtp_port: 587,
    imap_host: 'imap.aol.com',
    imap_port: 993,
    use_ssl: true,
    oauth_enabled: false,
  },
};

// Funktion zur automatischen Erkennung der Server-Einstellungen
export const detectEmailProvider = (email: string): { provider: EmailProvider | null; providerKey: string } => {
  if (!email || !email.includes('@')) {
    return { provider: null, providerKey: 'custom' };
  }
  
  const domain = email.split('@')[1].toLowerCase();
  const provider = EMAIL_DOMAIN_PROVIDERS[domain];
  
  if (provider) {
    // Suche den entsprechenden Provider-Key
    for (const [key, value] of Object.entries(EMAIL_PROVIDERS)) {
      if (value.name === provider.name) {
        return { provider, providerKey: key };
      }
    }
    // Falls kein bekannter Provider-Key gefunden wurde, verwende 'custom'
    return { provider, providerKey: 'custom' };
  }
  
  return { provider: null, providerKey: 'custom' };
};